import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';

import { appNow } from '../../../core/appTime';
import {
  appTimeActions,
  appTimeHasMock,
  appTimeStore,
  getMockedNow,
} from '../../../../store/appTimeStore';
import {
  DEVICE_LOCAL_TIMEZONE_OPTION_ID,
  MOCK_TIME_ZONE_OPTIONS,
} from '../../../../store/appTimeStore/constants';

const Page = styled.div`
  ${tw`w-full max-w-lg mx-auto px-4 py-6`}
`;

const Title = styled.h1`
  ${tw`text-xl font-semibold text-gray-900 mb-4`}
`;

const Card = styled.div`
  ${tw`bg-white border border-gray-200 rounded-lg p-4 mb-4`}
`;

const CardTitle = styled.h2`
  ${tw`text-sm font-semibold text-gray-900 mb-3`}
`;

const Row = styled.div`
  ${tw`mb-3`}
`;

const RowLabel = styled.p`
  ${tw`text-xs text-gray-500 mb-1`}
`;

const RowValue = styled.p`
  ${tw`text-sm text-gray-900`}
`;

const ToggleRow = styled.label`
  ${tw`flex items-center justify-between gap-3 py-2 mb-4 cursor-pointer`}
`;

const ToggleText = styled.div``;

const ToggleTitle = styled.p`
  ${tw`text-sm font-semibold text-gray-900`}
`;

const ToggleSubtitle = styled.p`
  ${tw`text-xs text-gray-500 mt-1`}
`;

const Field = styled.div`
  ${tw`mb-4`}
`;

const FieldLabel = styled.label`
  ${tw`block text-sm text-gray-600 mb-1`}
`;

const Select = styled.select`
  ${tw`w-full p-2 border border-gray-300 rounded`}
`;

const Input = styled.input`
  ${tw`w-full p-2 border border-gray-300 rounded`}
`;

const ButtonRow = styled.div`
  ${tw`flex gap-2 mb-4`}
`;

const OutlinedButton = styled.button`
  ${tw`flex-1 py-2 border border-gray-300 rounded text-sm font-medium text-gray-800 bg-white`}
  &:hover {
    ${tw`bg-gray-50`}
  }
`;

const ChipWrap = styled.div`
  ${tw`flex flex-wrap gap-2 mb-4`}
`;

const Chip = styled.button`
  ${tw`px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 bg-white text-gray-800`}
  &:hover {
    ${tw`bg-gray-50`}
  }
`;

const PrimaryButton = styled.button`
  ${tw`w-full py-3 mb-2 rounded-lg bg-gray-900 text-white font-semibold text-sm`}
  &:hover {
    ${tw`bg-gray-800`}
  }
`;

const SecondaryButton = styled.button`
  ${tw`w-full py-2 rounded-lg border border-gray-300 text-gray-800 font-medium text-sm bg-white`}
  &:hover {
    ${tw`bg-gray-50`}
  }
`;

const formatDisplay = (date: Date) => DateTime.fromJSDate(date).toFormat('yyyy/MM/dd h:mm a');

const timeZoneLabel = (timeZoneId: string | null | undefined): string => {
  if (!timeZoneId) {
    return 'Device local timezone';
  }
  return MOCK_TIME_ZONE_OPTIONS.find(z => z.id === timeZoneId)?.label ?? timeZoneId;
};

const nowInDraftZone = (timeZoneId: string | null): Date => {
  if (!timeZoneId) {
    return new Date();
  }
  return DateTime.now().setZone(timeZoneId).toJSDate();
};

const toDateInputValue = (d: Date) => DateTime.fromJSDate(d).toFormat('yyyy-MM-dd');

const toTimeInputValue = (d: Date) => DateTime.fromJSDate(d).toFormat('HH:mm');

const parseDraft = (dateStr: string, timeStr: string): Date => {
  const [y, m, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  return new Date(y, m - 1, day, hour, minute, 0, 0);
};

export default function MockAppTimePage() {
  const navigate = useNavigate();
  const appTime = useSnapshot(appTimeStore);

  const [draft, setDraft] = useState<Date>(() => getMockedNow() ?? new Date());
  const [draftTimeZoneId, setDraftTimeZoneId] = useState<string | null>(appTime.timeZoneId);

  useEffect(() => {
    setDraft(getMockedNow() ?? appNow());
    setDraftTimeZoneId(appTime.timeZoneId);
  }, [appTime.enabled, appTime.mockedUtcMs, appTime.timeZoneId]);

  const realNow = new Date();
  const effectiveNow = appNow();

  const selectValue = draftTimeZoneId ?? DEVICE_LOCAL_TIMEZONE_OPTION_ID;

  const applyPreset = (offset: { days?: number }, timeOfDay?: { hour: number; minute: number }) => {
    const base = nowInDraftZone(draftTimeZoneId);
    const next = DateTime.fromJSDate(base)
      .plus({ days: offset.days ?? 0 })
      .toJSDate();
    setDraft(
      new Date(
        next.getFullYear(),
        next.getMonth(),
        next.getDate(),
        timeOfDay?.hour ?? next.getHours(),
        timeOfDay?.minute ?? next.getMinutes(),
        0,
        0,
      ),
    );
  };

  return (
    <Page>
      <Title>Mock app time</Title>

      <Card>
        <CardTitle>Status</CardTitle>
        <Row>
          <RowLabel>Real device time</RowLabel>
          <RowValue>{formatDisplay(realNow)}</RowValue>
        </Row>
        <Row>
          <RowLabel>Effective app time</RowLabel>
          <RowValue>{formatDisplay(effectiveNow)}</RowValue>
        </Row>
        <Row>
          <RowLabel>Effective timezone</RowLabel>
          <RowValue>{timeZoneLabel(appTime.timeZoneId)}</RowValue>
        </Row>
        <Row>
          <RowLabel>Mode</RowLabel>
          <RowValue>{appTimeHasMock(appTime) ? 'Mocked' : 'Real time'}</RowValue>
        </Row>
      </Card>

      <ToggleRow>
        <ToggleText>
          <ToggleTitle>Enable mock time</ToggleTitle>
          <ToggleSubtitle>Persist across relaunches until cleared.</ToggleSubtitle>
        </ToggleText>
        <input
          type="checkbox"
          checked={appTimeHasMock(appTime)}
          onChange={e => {
            if (e.target.checked) {
              appTimeActions.enableMock(draft, draftTimeZoneId);
            } else {
              appTimeActions.clearMock();
            }
          }}
        />
      </ToggleRow>

      <Card>
        <CardTitle>Draft mock time</CardTitle>
        <Row>
          <RowLabel>Selected timezone</RowLabel>
          <RowValue>{timeZoneLabel(draftTimeZoneId)}</RowValue>
        </Row>
        <Row>
          <RowLabel>Selected local time</RowLabel>
          <RowValue>{formatDisplay(draft)}</RowValue>
        </Row>
      </Card>

      <Field>
        <FieldLabel>Timezone</FieldLabel>
        <Select
          value={selectValue}
          onChange={e => {
            const next = e.target.value === DEVICE_LOCAL_TIMEZONE_OPTION_ID ? null : e.target.value;
            setDraftTimeZoneId(next);
            appTimeActions.setTimeZone(next);
          }}
        >
          {MOCK_TIME_ZONE_OPTIONS.map(zone => (
            <option key={zone.id} value={zone.id}>
              {zone.label}
            </option>
          ))}
        </Select>
      </Field>

      <ButtonRow>
        <OutlinedButton
          type="button"
          onClick={() => {
            const picked = window.prompt('Date (YYYY-MM-DD)', toDateInputValue(draft));
            if (!picked) return;
            setDraft(parseDraft(picked, toTimeInputValue(draft)));
          }}
        >
          Pick date
        </OutlinedButton>
        <OutlinedButton
          type="button"
          onClick={() => {
            const picked = window.prompt('Time (HH:mm)', toTimeInputValue(draft));
            if (!picked) return;
            setDraft(parseDraft(toDateInputValue(draft), picked));
          }}
        >
          Pick time
        </OutlinedButton>
      </ButtonRow>

      <Field>
        <FieldLabel>Date</FieldLabel>
        <Input
          type="date"
          value={toDateInputValue(draft)}
          onChange={e => {
            if (!e.target.value) return;
            setDraft(parseDraft(e.target.value, toTimeInputValue(draft)));
          }}
        />
      </Field>

      <Field>
        <FieldLabel>Time</FieldLabel>
        <Input
          type="time"
          value={toTimeInputValue(draft)}
          onChange={e => {
            if (!e.target.value) return;
            setDraft(parseDraft(toDateInputValue(draft), e.target.value));
          }}
        />
      </Field>

      <ChipWrap>
        <Chip type="button" onClick={() => applyPreset({})}>
          Use now
        </Chip>
        <Chip type="button" onClick={() => applyPreset({}, { hour: 19, minute: 0 })}>
          Tonight 7:00 PM
        </Chip>
        <Chip type="button" onClick={() => applyPreset({ days: 7 }, { hour: 19, minute: 0 })}>
          +7 days 7:00 PM
        </Chip>
        <Chip type="button" onClick={() => setDraft(new Date(2026, 4, 30, 19, 0, 0, 0))}>
          May 30, 2026 7:00 PM
        </Chip>
      </ChipWrap>

      <PrimaryButton
        type="button"
        onClick={() => appTimeActions.enableMock(draft, draftTimeZoneId)}
      >
        Apply mock time
      </PrimaryButton>
      <SecondaryButton type="button" onClick={() => appTimeActions.clearMock()}>
        Clear mock time
      </SecondaryButton>
      <SecondaryButton type="button" onClick={() => navigate('/profile')}>
        Back to profile
      </SecondaryButton>
    </Page>
  );
}
